import { Button, Card, Empty } from 'antd'
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  PlusCircleOutlined,
  SettingOutlined,
  WalletOutlined,
} from '@ant-design/icons'
import type { ReactNode } from 'react'
import { ClassMonthlySummary } from '../types'

type MonthOverviewPanelProps = {
  loading: boolean
  hasSelection: boolean
  summary: ClassMonthlySummary
  defaultClassDurationMinutes: number
  onOpenMonthPlan?: () => void
  showMonthPlanButton?: boolean
}

type MetricTone = 'blue' | 'emerald' | 'sky' | 'amber' | 'rose' | 'violet'

function MetricLine({
  label,
  value,
  unit,
  valueClass,
}: {
  label: string
  value: string | number
  unit?: string
  valueClass: string
}) {
  return (
    <div className="flex min-h-[42px] flex-1 items-center justify-between gap-3 px-3 py-2 first:pt-2 last:pb-2">
      <div className="min-w-0 text-[12px] font-medium leading-4 text-slate-700">{label}</div>
      <div className="flex shrink-0 items-end gap-1">
        <span className={`text-[20px] font-semibold leading-none ${valueClass}`}>{value}</span>
        {unit ? <span className="pb-px text-[12px] font-semibold text-slate-700">{unit}</span> : null}
      </div>
    </div>
  )
}

function MetricCard({
  title,
  tone = 'blue',
  icon,
  rows,
}: {
  title: string
  tone?: MetricTone
  icon: ReactNode
  rows: Array<{ label: string; value: string | number; unit?: string }>
}) {
  const toneClasses: Record<
    MetricTone,
    { border: string; title: string; background: string; value: string }
  > = {
    blue: {
      border: 'border-blue-200/90',
      title: 'text-blue-600',
      background: 'bg-blue-50/60',
      value: 'text-blue-600',
    },
    emerald: {
      border: 'border-emerald-200/90',
      title: 'text-emerald-600',
      background: 'bg-emerald-50/60',
      value: 'text-emerald-600',
    },
    sky: {
      border: 'border-cyan-200/90',
      title: 'text-cyan-600',
      background: 'bg-cyan-50/60',
      value: 'text-cyan-600',
    },
    amber: {
      border: 'border-amber-200/90',
      title: 'text-orange-500',
      background: 'bg-amber-50/60',
      value: 'text-orange-500',
    },
    rose: {
      border: 'border-rose-200/90',
      title: 'text-rose-500',
      background: 'bg-rose-50/60',
      value: 'text-rose-500',
    },
    violet: {
      border: 'border-violet-200/90',
      title: 'text-violet-600',
      background: 'bg-violet-50/60',
      value: 'text-violet-600',
    },
  }
  const { border, title: toneTitle, background, value: valueClass } = toneClasses[tone]

  return (
    <div
      className={`flex min-h-[128px] flex-col rounded-[10px] border ${border} ${background} px-3 py-3 shadow-[0_14px_26px_-24px_rgba(15,23,42,0.35)]`}
    >
      <div className="flex items-center gap-2 px-1">
        <span className={`text-[18px] leading-none ${toneTitle}`}>{icon}</span>
        <span className={`text-[16px] font-semibold leading-none ${toneTitle}`}>{title}</span>
      </div>

      <div className="mt-3 flex flex-1 flex-col rounded-[8px] border border-slate-200/80 bg-white shadow-[0_10px_20px_-18px_rgba(15,23,42,0.45)]">
        {rows.map((row, index) => (
          <div
            key={row.label}
            className={`${index > 0 ? 'border-t border-dashed border-slate-200' : ''} flex-1`}
          >
            <MetricLine
              label={row.label}
              value={row.value}
              unit={row.unit}
              valueClass={valueClass}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

export function MonthOverviewPanel({
  loading,
  hasSelection,
  summary,
  defaultClassDurationMinutes,
  onOpenMonthPlan,
  showMonthPlanButton = true,
}: MonthOverviewPanelProps) {
  const leaveTrainingCount =
    summary.leaveMakeupTrainingCount + summary.leaveConvertedToMoneyTrainingCount
  const leaveGroupCount =
    summary.leaveMakeupGroupCount + summary.leaveConvertedToMoneyGroupCount
  const attendedTraining =
    summary.plannedAttendedTrainingCount + summary.manualAttendedTrainingCount
  const attendedGroup =
    summary.plannedAttendedGroupCount + summary.manualAttendedGroupCount
  const makeupTrainingLessons = Math.round(
    summary.makeupUsedMinutes / Math.max(defaultClassDurationMinutes, 1),
  )
  const makeupGroupLessons = summary.makeupUsedGroupLessons
  const cashTraining =
    summary.leaveConvertedToMoneyTrainingCount + summary.convertedToMoneyTrainingCount
  const cashGroup = summary.leaveConvertedToMoneyGroupCount + summary.convertedToMoneyGroupCount
  return (
    <Card
      size="small"
      title="当月摘要"
      loading={loading && hasSelection}
      className="rounded-[14px] border-slate-200 shadow-[0_14px_32px_-28px_rgba(15,23,42,0.45)] [&_.ant-card-body]:px-4 [&_.ant-card-body]:pb-4 [&_.ant-card-body]:pt-4 [&_.ant-card-head]:min-h-[52px] [&_.ant-card-head]:border-slate-200 [&_.ant-card-head]:px-4 [&_.ant-card-head-title]:text-[18px] [&_.ant-card-head-title]:font-semibold"
      extra={
        showMonthPlanButton ? (
          <Button
            size="middle"
            icon={<SettingOutlined />}
            disabled={!hasSelection}
            onClick={onOpenMonthPlan}
            className="h-[32px] rounded-[7px] px-3 text-[13px] font-medium"
          >
            月配置
          </Button>
        ) : null
      }
      >
      {hasSelection ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          <MetricCard
            title="计划"
            tone="blue"
            icon={<CalendarOutlined />}
            rows={[
              { label: '个训', value: summary.plannedTrainingCount, unit: '节' },
              { label: '集体', value: summary.plannedGroupCount, unit: '节' },
            ]}
          />
          <MetricCard
            title="已上"
            tone="emerald"
            icon={<CheckCircleOutlined />}
            rows={[
              { label: '个训', value: attendedTraining, unit: '节' },
              { label: '集体', value: attendedGroup, unit: '节' },
            ]}
          />
          <MetricCard
            title="请假"
            tone="amber"
            icon={<CloseCircleOutlined />}
            rows={[
              { label: '个训', value: leaveTrainingCount, unit: '节' },
              { label: '集体', value: leaveGroupCount, unit: '节' },
            ]}
          />
          <MetricCard
            title="补课"
            tone="rose"
            icon={<PlusCircleOutlined />}
            rows={[
              { label: '个训', value: summary.makeupUsedMinutes, unit: '分钟' },
              { label: '集体', value: makeupGroupLessons, unit: '节' },
            ]}
          />
          <MetricCard
            title="现金"
            tone="sky"
            icon={<WalletOutlined />}
            rows={[
              { label: '请假转金额', value: cashTraining, unit: '节' },
              { label: '转金额处理', value: cashGroup, unit: '节' },
              { label: '总金额', value: `¥${summary.convertedToMoneyAmount.toFixed(2)}` },
            ]}
          />
          <MetricCard
            title="余额"
            tone="violet"
            icon={<ClockCircleOutlined />}
            rows={[
              { label: '个训', value: summary.currentMakeupBalanceMinutes, unit: '分钟' },
              { label: '集体', value: summary.currentMakeupBalanceGroupLessons, unit: '节' },
            ]}
          />
        </div>
      ) : (
        <Empty description="选择学生和月份后显示当月摘要" />
      )}
    </Card>
  )
}
